import React, { useEffect, useState, useRef } from 'react';
import { ShieldCheck, Headset, CalendarClock, CreditCard, Star, Gift } from "lucide-react";

const features = [
  {
    title: "Verified & Trusted",
    description: "Only SSM-registered, DVS-certified pet hotels with business licenses and trained staff.",
    icon: <ShieldCheck className="w-6 h-6" />,
    color: "#f58849"
  },
  {
    title: "Instant Booking",
    description: "Browse real-time availability, compare certified pet hotels, and confirm your booking in seconds — all in one place.",
    icon: <CalendarClock className="w-6 h-6" />,
    color: "#f58849"
  },
  {
    title: "Transparent Pricing",
    description: "No hidden fees. Compare rates and pay exactly what you see upfront.",
    icon: <CreditCard className="w-6 h-6" />,
    color: "#f58849"
  },
  {
    title: "Pawsome Rewards",
    description: "Earn loyalty points with every booking. Redeem them for discounts, freebies, or special perks for your furry friend.",
    icon: <Gift className="w-6 h-6" />,
    color: "#f58849"
  },
  {
    title: "Verified Reviews",
    description: "We’re building our Stayfur community, so you’ll see reviews from Google until we gather more from our users — all to help you book with confidence.",
    icon: <Star className="w-6 h-6" />,
    color: "#f58849"
  },
  {
    title: "Stayfur Guarantee",
    description: "Peace of mind, always. We're here for you before, during, and after the stay.",
    icon: <Headset className="w-6 h-6" />,
    color: "#f58849"
  },
];

const FeaturesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the section enters the viewport
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Stop observing once triggered
        }
      },
      {
        root: null, // Use the viewport
        rootMargin: '0px',
        threshold: 0.2 // Trigger when 20% of the element is visible
      }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4 py-16 overflow-hidden relative">
      <div className={`text-center mb-12 transition-all duration-700 ease-out transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <h2 className="text-3xl font-bold text-gray-800 mb-3">
          Why Choose Us
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          We ensure the best care for your beloved pets
        </p>
        <div className={`h-1 w-24 bg-[#B46B42] mx-auto mt-4 rounded-full transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}></div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {features.map((feature, index) => (
          <div
            key={index}
            className={`text-center p-6 bg-white rounded-xl shadow-md transition-all duration-700 ease-out transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'}`}
            style={{ 
              transitionDelay: `${200 + index * 100}ms`,
              boxShadow: '0 4px 15px -3px rgba(0, 0, 0, 0.1)'
            }}
          >
            <div 
              className={`w-16 h-16 mx-auto mb-4 text-white rounded-full flex items-center justify-center transition-all duration-700 ease-out ${isVisible ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}
              style={{ 
                backgroundColor: feature.color,
                transitionDelay: `${400 + index * 100}ms`
              }}
            >
              {feature.icon}
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-800">
              {feature.title}
            </h3>
            <p className={`text-gray-600 transition-all duration-700 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
               style={{ transitionDelay: `${600 + index * 100}ms` }}>
              {feature.description}
            </p>
          </div>
        ))}
      </div>
      
      {/* Decorative elements that fade in */}
      <div className={`absolute -left-16 top-1/4 w-32 h-32 bg-[#B46B42] rounded-full blur-3xl transition-opacity duration-1000 ${isVisible ? 'opacity-5' : 'opacity-0'}`}></div>
      <div className={`absolute -right-16 bottom-1/4 w-32 h-32 bg-[#B46B42] rounded-full blur-3xl transition-opacity duration-1000 delay-500 ${isVisible ? 'opacity-5' : 'opacity-0'}`}></div>
    </div>
  );
};

export default FeaturesSection;